import * as THREE from 'three';
import { MeshToonNodeMaterial } from 'three/webgpu';

import { EngineClass } from '../../../systems/ClassRegistry.js';
import { property } from '../../../utils/serialization/decorator.js';
import { ISerializableObjectTag } from '../../../utils/serialization/serializable.js';

import { NodeMaterialAsset } from './NodeMaterialAsset.js';

import type { TexturePath } from '../../../utils/serialization/decorator.js';
import type { IDumper, ILoader } from '../../../utils/serialization/factories.js';

/** WebGPU `MeshToonNodeMaterial` asset: stylized toon/cel shading with authored diffuse color. */
@EngineClass('MeshToonNodeMaterialAsset', {
  isNodeMaterialAsset: true,
  nodeMaterialDisplayName: 'Mesh Toon Node Material',
  nodeMaterialGroup: 'TSL Node (THREE)',
})
export class MeshToonNodeMaterialAsset extends NodeMaterialAsset(MeshToonNodeMaterial) {
  readonly [ISerializableObjectTag] = true as const;

  @property({ type: 'color', description: 'Diffuse color' })
  override color = new THREE.Color(1.0, 1.0, 1.0);

  @property({ type: 'number', min: -10, max: 10, step: 0.01, description: 'Bump map influence' })
  override bumpScale = 1.0;

  @property({ type: 'vector2', description: 'Normal map influence' })
  override normalScale = new THREE.Vector2(1, 1);

  @property({ type: 'number', description: 'Normal map type (`THREE.TangentSpaceNormalMap`, `ObjectSpaceNormalMap`)' })
  override normalMapType = THREE.TangentSpaceNormalMap;

  @property({ type: 'texturePath', description: 'Color map path' })
    mapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Gradient map path' })
    gradientMapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Alpha map path' })
    alphaMapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Normal map path' })
    normalMapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Bump map path' })
    bumpMapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Emissive map path' })
    emissiveMapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Displacement map path' })
    displacementMapPath: TexturePath = '';

  constructor() {
    super();
    this.rebuild();
  }

  override rebuild(): void {
    this.applyCommonMaterialState();
    this.bumpScale = this.clampFiniteNumber(this.bumpScale, -10, 10, 1.0);
    this.syncTexturePath('map', this.mapPath, THREE.SRGBColorSpace);
    this.syncTexturePath('gradientMap', this.gradientMapPath, THREE.NoColorSpace);
    this.syncTexturePath('alphaMap', this.alphaMapPath, THREE.NoColorSpace);
    this.syncTexturePath('normalMap', this.normalMapPath, THREE.NoColorSpace);
    this.syncTexturePath('bumpMap', this.bumpMapPath, THREE.NoColorSpace);
    this.syncTexturePath('emissiveMap', this.emissiveMapPath, THREE.SRGBColorSpace);
    this.syncTexturePath('displacementMap', this.displacementMapPath, THREE.NoColorSpace);
    this.colorNode = null;
  }

  public serialize(dumper: IDumper): void {
    this.serializeAuthoredFields(dumper);
  }

  public static staticDeserialize(data: unknown, loader: ILoader): MeshToonNodeMaterialAsset {
    const instance = new MeshToonNodeMaterialAsset();
    MeshToonNodeMaterialAsset.loadAuthoredFields(instance, loader);
    instance.rebuild();
    return instance;
  }
}
